import {FC, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {Button} from '@components/ui/button/Button.tsx'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {MultiSelect} from '@components/ui/multi-select/MultiSelect.tsx'
import {MultiSelectBase} from '@components/ui/multi-select/MultiSelectBase.tsx'
import {TextInputFilter} from '@components/ui/text-input-filter/TextInputFilter.tsx'
import {FilterFieldNames, FilterFormValues, useFilterMeasures} from '@hooks/useFilterMeasures.tsx'
import {useGetFiltersLabel} from '@/features/measures/services/useGetFiltersLabel.ts'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {ScrollableFlexbox} from './style'

type MeasureFiltersDesktopProps = {
    filterFields: Record<
        Exclude<FilterFieldNames, 'activeTab' | 'page' | 'perPage'>,
        {
            label: string
            icon: ReactNode
            options: {id: string; name: string}[]
            isFetchingNextPage?: boolean
            fetchNextPage?: () => void
            hasNextPage?: boolean
            setSearchSections?: (value: string) => void
            isLoading?: boolean
        }
    >
    debouncedSubmit: (data: Partial<FilterFormValues>) => void
    filterSelected: (keyof FilterFormValues)[]
    watchedFilters: FilterFormValues
}

export const MeasureFiltersDesktop: FC<MeasureFiltersDesktopProps> = ({
    filterFields,
    debouncedSubmit,
    filterSelected,
    watchedFilters
}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const {resetFilters} = useFilterMeasures()

    const {isLoading: isLoadingFiltersLabels, updatedFilters} = useGetFiltersLabel(watchedFilters)

    const hasMissingNames = Object.entries(watchedFilters).some(([_, value]) => {
        if (Array.isArray(value)) {
            return value.some(item => !item.name && item.id)
        }
        return false
    })

    const displayItem = hasMissingNames && !isLoadingFiltersLabels ? updatedFilters : watchedFilters

    return (
        <Flexbox direction={'column'} gap={4} fullWidth justify={'flex-start'}>
            <Flexbox style={{flexWrap: 'wrap'}} gap={3} fullWidth justify={'flex-end'} align={'center'}>
                <MultiSelect
                    value={watchedFilters.counter_part}
                    options={filterFields.counter_part.options}
                    displayLabel={t(filterFields.counter_part.label)}
                    displayIcon={filterFields.counter_part.icon}
                    searchPlaceholder={t('measures:filters:select_counter_parts')}
                    onChange={selectedValues => debouncedSubmit({counter_part: selectedValues})}
                    isFetchingNextPage={filterFields.counter_part.isFetchingNextPage}
                    fetchNextPage={filterFields.counter_part.fetchNextPage}
                    hasNextPage={filterFields.counter_part.hasNextPage}
                    setSearchSections={filterFields.counter_part.setSearchSections}
                    isLoading={filterFields.counter_part.isLoading}
                />

                <MultiSelectBase
                    value={watchedFilters.type_of_measure}
                    options={filterFields.type_of_measure.options}
                    displayLabel={t(filterFields.type_of_measure.label)}
                    displayIcon={filterFields.type_of_measure.icon}
                    searchPlaceholder={t('measures:filters:select_type_of_measures')}
                    onChange={selectedValues => debouncedSubmit({type_of_measure: selectedValues})}
                    isLoading={filterFields.type_of_measure.isLoading}
                />

                <TextInputFilter
                    value={watchedFilters.general_number}
                    onChange={value => debouncedSubmit({general_number: value})}
                    displayLabel={t(filterFields.general_number.label)}
                    displayIcon={filterFields.general_number.icon}
                    placeholder={t('measures:filters:general_number')}
                    sameWidth={false}
                    multiple={false}
                />

                <MultiSelectBase
                    value={watchedFilters.general_role}
                    options={filterFields.general_role.options}
                    displayLabel={t(filterFields.general_role.label)}
                    displayIcon={filterFields.general_role.icon}
                    searchPlaceholder={t('measures:filters:general_role')}
                    onChange={selectedValues => debouncedSubmit({general_role: selectedValues})}
                    isLoading={filterFields.general_role.isLoading}
                />

                <MultiSelect
                    value={watchedFilters.site}
                    options={filterFields.site.options}
                    displayLabel={t(filterFields.site.label)}
                    displayIcon={filterFields.site.icon}
                    searchPlaceholder={t('measures:filters:select_sites')}
                    onChange={selectedValues => debouncedSubmit({site: selectedValues})}
                    isFetchingNextPage={filterFields.site.isFetchingNextPage}
                    fetchNextPage={filterFields.site.fetchNextPage}
                    hasNextPage={filterFields.site.hasNextPage}
                    setSearchSections={filterFields.site.setSearchSections}
                    isLoading={filterFields.site.isLoading}
                />

                <MultiSelect
                    value={watchedFilters.section}
                    options={filterFields.section.options}
                    displayLabel={t(filterFields.section.label)}
                    displayIcon={filterFields.section.icon}
                    searchPlaceholder={t('measures:filters:select_sections')}
                    onChange={selectedValues => debouncedSubmit({section: selectedValues})}
                    isFetchingNextPage={filterFields.section.isFetchingNextPage}
                    fetchNextPage={filterFields.section.fetchNextPage}
                    hasNextPage={filterFields.section.hasNextPage}
                    setSearchSections={filterFields.section.setSearchSections}
                    isLoading={filterFields.section.isLoading}
                />

                <MultiSelect
                    value={watchedFilters.panel}
                    options={filterFields.panel.options}
                    displayLabel={t(filterFields.panel.label)}
                    displayIcon={filterFields.panel.icon}
                    searchPlaceholder={t('measures:filters:select_panels')}
                    onChange={selectedValues => debouncedSubmit({panel: selectedValues})}
                    isFetchingNextPage={filterFields.panel.isFetchingNextPage}
                    fetchNextPage={filterFields.panel.fetchNextPage}
                    hasNextPage={filterFields.panel.hasNextPage}
                    setSearchSections={filterFields.panel.setSearchSections}
                    isLoading={filterFields.panel.isLoading}
                />
            </Flexbox>

            {filterSelected.length > 0 && (
                <ScrollableFlexbox direction={'row'} align={'center'} justify={'flex-end'} gap={2} fullWidth>
                    {filterSelected.map(key => {
                        const filterLabel = filterFields[key as keyof typeof filterFields]?.label || ''

                        if (key === 'general_number') {
                            const value = watchedFilters.general_number
                            return value ? (
                                <Chip
                                    key={`${key}-${value}`}
                                    textColor={theme.palette.neutral[700]}
                                    bgColor={theme.palette.neutral[100]}
                                    text={`${t(filterLabel)}: ${value}`}
                                    onDelete={() => debouncedSubmit({[key]: ''})}
                                />
                            ) : null
                        }

                        return (displayItem[key] as {id: string; name: string}[]).map(item => (
                            <Chip
                                key={`${key}-${item.id}`}
                                textColor={theme.palette.neutral[700]}
                                bgColor={theme.palette.neutral[100]}
                                text={
                                    hasMissingNames && isLoadingFiltersLabels
                                        ? `${t(filterLabel)}: ${t('measures:filters:loading')}`
                                        : key === 'type_of_measure'
                                          ? `${t(filterLabel)}: ${t(`measures:type:${item.id}`) || item.name}`
                                          : `${t(filterLabel)}: ${item.name || item.id}`
                                }
                                icon={hasMissingNames && isLoadingFiltersLabels ? <Spinner size={16} /> : undefined}
                                onDelete={() => {
                                    const updatedValues = (displayItem[key] as {id: string; name: string}[]).filter(
                                        v => v.id !== item.id
                                    )
                                    debouncedSubmit({[key]: updatedValues})
                                }}
                            />
                        ))
                    })}
                </ScrollableFlexbox>
            )}

            {filterSelected.length > 0 && (
                <Flexbox direction="row" justify="flex-end" fullWidth>
                    <Button variant="primaryGhost" onClick={resetFilters}>
                        {t('measures:filters:reset_filters')}
                    </Button>
                </Flexbox>
            )}
        </Flexbox>
    )
}
